import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_URL from "../Config";
import axiosInstance from "../axiosinstance";
import "./tickets.css";
import PaginationFooter from "../Components/PaginationFooter";

import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CheckCircleOutline,
  DeleteOutline,
  DoneAll,
  OpenInNew,
  Search,
} from "@mui/icons-material";
import Loader from "../Components/Loader";
import { ToastContainer, toast, Bounce } from "react-toastify";
import { getNotificationDotColor } from "../utils/notificationColors";
import { getNotificationRoute } from "../utils/notificationRoutes";
import {
  isTicketNotification,
  buildTicketNotificationOpenUrl,
} from "../utils/ticketNotificationOpen";

const normalizeText = (value) => String(value || "").toLowerCase();
const NOTIFICATIONS_TOAST_CONTAINER_ID = "notifications-page-toast";
const DELETE_NOTIFICATION_TOAST_ID = "notification-delete-success";

const toLower = (value) => String(value || "").trim().toLowerCase();

const parseNotificationCreatedAt = (createdAt) => {
  if (!createdAt) return 0;

  const value = String(createdAt).trim();
  const match = value.match(
    /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?(?:\s+[A-Za-z]{2,5})?$/,
  );

  if (match) {
    const [, day, month, year, hour = "0", minute = "0", second = "0"] = match;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    ).getTime();
  }

  const parsedTimestamp = Date.parse(value);
  return Number.isNaN(parsedTimestamp) ? 0 : parsedTimestamp;
};

const formatRelativeNotificationTime = (createdAt, timezone) => {
  const withTimezone = (val) =>
    timezone ? `${val} ${String(timezone).trim()}` : val;

  const timestamp = parseNotificationCreatedAt(createdAt);
  const fallback = withTimezone(String(createdAt || "").trim()).trim();
  if (!timestamp) return fallback;

  const diffMs = Date.now() - timestamp;
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;

  if (diffMs < 0) return fallback;
  if (diffMs < minuteMs) return withTimezone("just now");
  if (diffMs < hourMs) return withTimezone(`${Math.floor(diffMs / minuteMs)}m ago`);
  if (diffMs < dayMs) return withTimezone(`${Math.floor(diffMs / hourMs)}h ago`);
  if (diffMs < 2 * dayMs) return withTimezone("yesterday");

  const date = new Date(timestamp);
  const isSameYear = date.getFullYear() === new Date().getFullYear();
  const formattedDate = new Intl.DateTimeFormat(
    "en-IN",
    isSameYear
      ? { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }
      : { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" },
  ).format(date);
  return withTimezone(formattedDate);
};

const filterNotifications = (notificationList, query) => {
  const normalizedQuery = normalizeText(query).trim();

  if (!normalizedQuery) return notificationList;

  return notificationList.filter((notification) => {
    const title = normalizeText(notification.title);
    const message = normalizeText(notification.message);
    const referenceType = normalizeText(notification.reference_type);

    return (
      title.includes(normalizedQuery) ||
      message.includes(normalizedQuery) ||
      referenceType.includes(normalizedQuery)
    );
  });
};

const NotificationRow = ({
  notification,
  isLast,
  onOpen,
  onDelete,
  onMarkRead,
}) => {
  const relativeTime = formatRelativeNotificationTime(
    notification.created_at,
    notification.timezone,
  );

  return (
    <Box
      onClick={() => onOpen(notification)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(notification);
        }
      }}
      sx={{
        px: 2.5,
        py: 1.8,
        borderBottom: isLast ? "none" : "1px solid #eceff3",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.2,
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        backgroundColor: notification.is_read ? "#fff" : "#f8fbff",
        "&:hover": { backgroundColor: "#f4f7fb" },
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          minWidth: 8,
          minHeight: 8,
          marginTop: "7px",
          marginRight: "4px",
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: getNotificationDotColor(notification.title),
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "1rem",
            lineHeight: 1.25,
            color: "#111827",
            fontWeight: notification.is_read ? 600 : 700,
            mb: 0.4,
          }}
        >
          {notification.title || "Untitled Notification"}
        </Typography>
        <Typography
          sx={{
            color: "#556274",
            fontSize: "0.9rem",
            lineHeight: 1.35,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {notification.message || "No message provided"}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          ml: 1,
          alignSelf: "center",
        }}
      >
        <Typography
          sx={{
            color: "#6b7280",
            fontSize: "0.9rem",
            minWidth: "90px",
            textAlign: "right",
            mr: 0.5,
          }}
        >
          {relativeTime}
        </Typography>

        {!notification.is_read ? (
          <Tooltip title="Mark as read">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onMarkRead(notification.id);
              }}
            >
              <DoneAll sx={{ fontSize: 20, color: "#2563EB" }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Read">
            <span>
              <IconButton size="small" disabled>
                <CheckCircleOutline sx={{ fontSize: 20, color: "#9ca3af" }} />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title="Delete notification">
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onDelete(notification.id);
            }}
          >
            <DeleteOutline sx={{ fontSize: 20, color: "#374151" }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Open">
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onOpen(notification);
            }}
          >
            <OpenInNew sx={{ fontSize: 19, color: "#374151" }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const Notifications = ({ isSidebarExpanded }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [pageLoading, setPageLoading] = useState(false);
  const isInitialLoad = useRef(true);

  const fetchNotifications = async (page = 1) => {
    setLoading(true);
    setError(null);

    if (isInitialLoad.current) {
      setPageLoading(true);
    }

    try {
      const response = await axiosInstance.get(`${API_URL}/api/notifications`, {
        params: { page, limit: itemsPerPage },
      });

      const list =
        response.data?.notifications || response.data?.data || response.data?.rows || [];
      const total =
        response.data?.total ??
        response.data?.meta?.total ??
        response.data?.count ??
        list.length;

      setNotifications(list);
      setTotalRecords(total);
    } catch (err) {
      if (err.response?.status === 403) {
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Access denied. You don't have permission to view these notifications.",
        );
        toast.error("Access denied to notifications");
      } else if (err.response?.status === 401) {
        setError("Session expired. Please login again.");
      } else {
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Failed to fetch notifications.",
        );
      }
    } finally {
      setLoading(false);
      if (isInitialLoad.current) {
        setPageLoading(false);
        isInitialLoad.current = false;
      }
    }
  };

  const deleteNotification = async (id) => {
    try {
      await axiosInstance.delete(`${API_URL}/api/fcm/delete_notification/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setTotalRecords((prev) => Math.max(prev - 1, 0));
      toast.dismiss(DELETE_NOTIFICATION_TOAST_ID);
      toast.success("Notification deleted successfully", {
        autoClose: 4000,
        containerId: NOTIFICATIONS_TOAST_CONTAINER_ID,
        toastId: DELETE_NOTIFICATION_TOAST_ID,
      });
      window.dispatchEvent(new Event("notificationUpdated"));
    } catch (err) {
      toast.dismiss(DELETE_NOTIFICATION_TOAST_ID);
      toast.error(err.response?.data?.message || "Failed to delete notification", {
        autoClose: 4000,
        containerId: NOTIFICATIONS_TOAST_CONTAINER_ID,
        toastId: DELETE_NOTIFICATION_TOAST_ID,
      });
    }
  };

  const markNotificationRead = async (id) => {
    try {
      await axiosInstance.post(`${API_URL}/mark_read/${id}`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
      );
      window.dispatchEvent(new Event("notificationUpdated"));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const navigateToReferenceToTicket = async (notification) => {
    const role = toLower(localStorage.getItem("role_name"));
    const isAdmin = role === "admin";

    if (isTicketNotification(notification) && !isAdmin) {
      navigate(buildTicketNotificationOpenUrl(notification));
      return;
    }

    const route = getNotificationRoute(notification);
    if (route) {
      navigate(route);
    }
  };

  const handleOpenNotification = async (notification) => {
    if (!notification.is_read) {
      markNotificationRead(notification.id);
    }
    await navigateToReferenceToTicket(notification);
  };

  const filteredNotifications = useMemo(
    () => filterNotifications(notifications, searchQuery),
    [notifications, searchQuery],
  );

  const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage));

  useEffect(() => {
    fetchNotifications(currentPage);
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  return (
    <div>
      <Box
        className={`table-container equipment-container ${isSidebarExpanded ? "expanded" : "collapsed"}`}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 104px)",
          overflow: "hidden",
          minHeight: 0,
        }}
        style={{ padding: "10px 18px 0px" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Box>
            <Typography className="page-title" sx={{ margin: 0 }}>
              Notifications
            </Typography>
            
          </Box>

          <TextField
            placeholder="Search notifications..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            sx={{
              width: { xs: "100%", sm: 340, md: 420 },
              "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {pageLoading ? (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backdropFilter: "blur(6px)",
              background: "rgba(0,0,0,0.3)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                transform: "scale(2)",
                gap: "10px",
              }}
            >
              <Loader />
            </Box>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                border: "1px solid #d9dee7",
                borderBottom: "none",
                borderRadius: "14px 14px 0 0",
                backgroundColor: "#fff",
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
            {loading ? (
              <Box
                sx={{
                  minHeight: 220,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ color: "#6b7280" }}>Loading notifications...</Typography>
              </Box>
            ) : notifications.length === 0 ? (
              <Box
                sx={{
                  minHeight: 220,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ color: "#6b7280" }}>No notifications available</Typography>
              </Box>
            ) : filteredNotifications.length === 0 ? (
              <Box
                sx={{
                  minHeight: 220,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ color: "#6b7280" }}>No notifications found</Typography>
              </Box>
            ) : (
              filteredNotifications.map((notification, index) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  isLast={index === filteredNotifications.length - 1}
                  onOpen={handleOpenNotification}
                  onDelete={deleteNotification}
                  onMarkRead={markNotificationRead}
                />
              ))
            )}
            </Box>
            <Box sx={{ position: "sticky", bottom: 0, backgroundColor: "#fff", zIndex: 10 }}>
              <PaginationFooter
                totalRecords={totalRecords}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                setCurrentPage={setCurrentPage}
                setItemsPerPage={setItemsPerPage}
              />
            </Box>
          </Box>
        )}
      </Box>

      <ToastContainer
        containerId={NOTIFICATIONS_TOAST_CONTAINER_ID}
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
        closeButton
      />
    </div>
  );
};

export default Notifications;
